// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseFilters,
  BaseInputSearch,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
  SortComponent,
  UnderDevelopment,
} from '~/components/common';
// Others
import useDebounce from '~/utils/hooks/useDebounce';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SORT_INVENTORY,
  EMPTY_STRING,
  EMPTY_VALUE,
  FILTER_DEFAULT_VALUES,
  optionStatusInventory,
} from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { IPaginationResponse } from '~/utils/interface/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import {
  IBaseMoreAction,
  IBaseOption,
  IFilterValueChange,
  IHeaderCellType,
  IResultCallApiFn,
  ISortBy,
} from '~/utils/interface/common';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { IInventoryProduct, IParamInventoryProduct } from '~/utils/interface/inventory';
import { inventoryActions, selectIsRefreshListInventory } from '~/thunks/inventory/inventorySlice';
import {
  deleteInventory,
  exportInventory,
  getListInventoryProduct,
  importInventory,
} from '~/thunks/inventory/inventoryThunk';
import { AcceptFileEnum, RoleEnum, ToastTypeEnum, TypeListInventory } from '~/utils/enum/common';
import { customToast, formatCurrencyUSD } from '~/utils/helper/common';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { PRODUCT_PERMISSIONS } from '~/utils/constants/permissions';
import { usePermissions } from '~/utils/hooks/userPermissions';
// Styles, images, icons
import styles from './Inventory.module.scss';
import { icons } from '~/assets';
import { CloneProductForm } from '~/components/module/inventory';

type Props = {};

const cx = classNames.bind(styles);

const columnsInventory = (
  t: TFunction,
  sortBy: ISortBy[],
  handleSort: (sortType: string, name: string) => void,
  onView: (id: string, item: IInventoryProduct) => void,
  onEdit: (id: string) => void,
  onClone: (id: string) => void,
  onArchive: (id: string) => void,
  isDetail: boolean,
  isEdit: boolean
): IHeaderCellType<IInventoryProduct>[] => {
  const getSortType = (name: string) => sortBy.find((item) => item.name === name)?.typeSort || EMPTY_STRING;

  return [
    {
      id: 'name',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_product_name')}</Box>
          <SortComponent name='name' sortType={getSortType('name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInventoryProduct) => (
        <Stack direction={'row'} gap={2} alignItems={'center'}>
          <Stack gap={1}>
            <Box className={cx('textName')}>{record.name}</Box>
          </Stack>
          {record.type === TypeListInventory.PART && (
            <Box className={cx('addOne')}>{t('table_value_title_add_on')}</Box>
          )}
        </Stack>
      ),
      minWidth: 200,
    },
    {
      id: 'variantName',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_variant_name')}</Box>
          <SortComponent name='variant name' sortType={getSortType('variant name')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInventoryProduct) => <Box className={cx('textName')}>{record.variantName || EMPTY_VALUE}</Box>,
      minWidth: 150,
    },
    {
      id: 'category',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_category')}</Box>
          <SortComponent name='category' sortType={getSortType('category')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInventoryProduct) => (
        <Box className={cx('textValue')}>{record?.category?.name || EMPTY_VALUE}</Box>
      ),
      minWidth: 110,
    },
    {
      id: 'brand',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_brand')}</Box>
          <SortComponent name='brand' sortType={getSortType('brand')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInventoryProduct) => <Box className={cx('textValue')}>{record?.brand || EMPTY_VALUE}</Box>,
      minWidth: 110,
    },
    {
      id: 'available',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_available')}</Box>
          <SortComponent name='available' sortType={getSortType('available')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInventoryProduct) => <Box className={cx('textValue')}>{record.quantity}</Box>,
      minWidth: 110,
    },
    {
      id: 'rentalPrice',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_rental_price')}</Box>
          <SortComponent name='rental price' sortType={getSortType('rental price')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInventoryProduct) => (
        <Box className={cx('textValue')}>{formatCurrencyUSD({ value: record?.rentalPrice, showDecimal: true })}</Box>
      ),
      minWidth: 110,
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('table_header_title_status')}</Box>
          <SortComponent name='status' sortType={getSortType('status')} handleSort={handleSort} />
        </Stack>
      ),
      render: (record: IInventoryProduct) =>
        record?.status ? <BaseStatus label={record?.status || EMPTY_STRING} /> : EMPTY_VALUE,
    },
    {
      id: 'action',
      label: t('table_header_title_actions'),
      minWidth: 100,
      align: 'center',
      render: (record: IInventoryProduct) => {
        const actions: IBaseMoreAction[] = [
          ...(isDetail
            ? [
                {
                  label: t('table_action_view', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionView}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onView(record.id, record),
                },
              ]
            : []),
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record.id),
                },
              ]
            : []),
          {
            label: t('table_action_clone', { ns: 'common/base' }),
            icon: (
              <img
                src={icons.commonIconClone}
                alt={t('common_alt_img', { ns: 'common/base' })}
                width={16}
                height={16}
              />
            ),
            onClick: () => onClone(record.id),
          },
          // {
          //   label: t('table_action_make_broken_repair', { ns: 'common/base' }),
          //   icon: (
          //     <img
          //       src={icons.tableIconActionActivate}
          //       alt={t('common_alt_img', { ns: 'common/base' })}
          //       width={16}
          //       height={16}
          //     />
          //   ),
          //   hasOtherColor: true,
          //   onClick: () => onMake(record.id),
          // },
          // {
          //   label: t('table_action_archive', { ns: 'common/base' }),
          //   icon: (
          //     <img
          //       src={icons.tableIconActionDelete}
          //       alt={t('common_alt_img', { ns: 'common/base' })}
          //       width={16}
          //       height={16}
          //     />
          //   ),
          //   hasOtherColor: true,
          //   onClick: () => onArchive(record.id),
          // },
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Inventory = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canCreateProducts, canViewProducts, canDetailProducts, canEditProducts } =
    usePermissions(PRODUCT_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/inventory', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);

  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  const isRefreshListInventory = useSelector(selectIsRefreshListInventory);
  //#endregion Selector

  //#region Declare State
  const [sortBy, setSortBy] = useState<ISortBy[]>(DEFAULT_SORT_INVENTORY);
  const [filterParams, setFilterParams] = useState<IParamInventoryProduct>(FILTER_DEFAULT_VALUES);
  const [searchKey, setSearchKey] = useState<string>(EMPTY_STRING);
  const debounceSearchKey = useDebounce(searchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [paramObject, setParamObject] = useState<IParamInventoryProduct>({
    page: pageSelected,
    limit: limitPage,
    isHasVariant: true,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [inventoryList, setInventoryList] = useState<IInventoryProduct[]>([]);
  const [totalItems, setTotalItems] = useState<number>(0);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);

  const [idsInventory, setIdsInventory] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<IInventoryProduct[]>([]);
  const [idInventory, setIdInventory] = useState<string>(EMPTY_STRING);

  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);
  const [isShowFormCloneProduct, setIsShowFormCloneProduct] = useState<boolean>(false);

  const importInventoryRef = useRef<HTMLInputElement | null>(null);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListInventory(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshListInventory) return;

    handleGetListInventory(paramObject);

    dispatch(inventoryActions.setRefreshInventoryList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshListInventory]);

  useEffect(() => {
    if (pagination || debounceSearchKey) {
      setParamObject({
        ...paramObject,
        page: DEFAULT_CURRENT_PAGE,
        limit: limitPage,
        ...(debounceSearchKey ? { textSearch: debounceSearchKey } : { textSearch: undefined }),
        ...(filterParams ? { ...filterParams } : {}),
      });

      setSearchParams({
        page: String(DEFAULT_CURRENT_PAGE),
        limit: String(limitPage),
        ...(searchKey ? { textSearch: debounceSearchKey } : {}),
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceSearchKey]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListInventory = (params: IParamInventoryProduct) => {
    setIsLoading(true);

    dispatch(getListInventoryProduct(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination, totalItems } = res?.data;

        if (!responses) return;

        setInventoryList(responses);
        setPagination(pagination);

        setTotalItems(totalItems);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleAdd = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.createInventory);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.createInventory);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.createInventory);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.createInventory);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.createInventory);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.createInventory);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.createInventory);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.createInventory);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.createInventory);
        break;
    }
  };

  const handleEdit = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateInventory(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateInventory(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateInventory(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateInventory(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateInventory(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateInventory(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateInventory(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateInventory(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateInventory(id));
        break;
    }
  };

  const handleView = (id: string) => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.detailInventory(id));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.detailInventory(id));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.detailInventory(id));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.detailInventory(id));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.detailInventory(id));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.detailInventory(id));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.detailInventory(id));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.detailInventory(id));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.detailInventory(id));
        break;
    }
  };

  const handleClone = (id: string) => {
    setIsShowFormCloneProduct(true);
    setIdInventory(id);
  };

  const handleCloseFormCloneProduct = () => {
    setIsShowFormCloneProduct(false);
    setIdInventory(EMPTY_STRING);
  };

  const handleArchive = (id: string) => {
    if (!id) return;

    setIdInventory(id);
    setIsShowConfirmDelete(true);
  };

  const handleCloseConfirmDelete = () => {
    setIdInventory(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteInventory(idInventory))
      .unwrap()
      .then(() => {
        setIdInventory(EMPTY_STRING);
        handleCloseConfirmDelete();
        dispatch(inventoryActions.setRefreshInventoryList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleImport = () => {
    importInventoryRef?.current?.click();
  };

  const handleImportInventoryChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileUpload = event?.target?.files?.[0];

    if (fileUpload) {
      if (!fileUpload.name.endsWith(AcceptFileEnum.CSV)) {
        customToast(ToastTypeEnum.WARNING, t('common_warning_csv_file_import', { ns: 'common/base' }));
        return;
      }

      const formData = new FormData();
      formData.append('file', fileUpload);

      setIsLoading(true);

      dispatch(importInventory(formData))
        .unwrap()
        .then((res) => {
          dispatch(inventoryActions.setRefreshInventoryList(true));
        })
        .catch((_error) => {})
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IParamInventoryProduct = {
      textSearch: paramObject?.textSearch,
      status: paramObject?.status,
      sortBy: paramObject?.sortBy,
      sortType: paramObject?.sortType,
    };

    dispatch(exportInventory(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleRowsSelect = (ids: string[], selectedItems?: IInventoryProduct[]) => {
    setIdsInventory(ids);
    setSelectedItems(selectedItems || []);
  };

  const handleSearchChange = (value: string) => {
    setSearchKey(value);
  };

  const handleSort = (sortType: string, name: string) => {
    const updatedSortBy = sortBy.map((item) => ({
      ...item,
      typeSort: item.name === name ? sortType : undefined,
    }));
    setSortBy(updatedSortBy);
    setParamObject({ ...paramObject, sortBy: name, sortType: sortType });
    setSearchParams({ ...params, sortBy: name, sortType: sortType });
  };

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionStatusInventory,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleApplyFilter = (values: IParamInventoryProduct | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        status: values?.status,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      status: undefined,
    });
  };

  const handleFilterStatusChange = (
    status: IBaseOption,
    onChange?: (value: IFilterValueChange<IParamInventoryProduct>) => void
  ) => {
    if (!status) return;

    const { value } = status;

    onChange?.({ name: 'status', value: value });
    setFilterParams({ ...filterParams, status: value });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };
  //#endregion Handle Function

  return (
    <Box id='inventoryPage' className={cx('container')}>
      <BaseToolbar title={t('inventory_title_inventory_management')}>
        <BaseInputSearch
          width={200}
          typeStyle='outlined'
          onChange={handleSearchChange}
          borderRadius={100}
          placeholder={t('inventory_title_search')}
        />

        <BaseFilters
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseSelect
                gap={10}
                height={40}
                styleType='contained'
                label={t('inventory_title_filter_status')}
                placeholder={t('inventory_title_filter_all_status')}
                callApiFn={fetchOptionFilter}
                value={value?.status}
                onChange={(option) => handleFilterStatusChange(option as any, onChange)}
              />
            </Stack>
          )}
        </BaseFilters>

        {canViewProducts && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleImport}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('common_button_import_excel', { ns: 'common/base' })}
            </Button>

            <input
              type='file'
              className={cx('inputImport')}
              ref={importInventoryRef}
              accept={AcceptFileEnum.CSV}
              onChange={handleImportInventoryChange}
            />
          </Box>
        )}

        {canCreateProducts && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='contained'
              className={cx('buttonAction')}
              onClick={handleAdd}
              startIcon={<img src={icons.commonIconPlus} alt={'common_alt_img'} />}
            >
              {t('common_text_add_item', { ns: 'common/base' })}
            </Button>
          </Box>
        )}

        {canViewProducts && (
          <Box className={cx('button')}>
            <Button
              type='button'
              variant='outlined'
              className={cx('buttonExport')}
              disabled={inventoryList.length === 0}
              onClick={handleExport}
            >
              {t('common_button_export_excel', { ns: 'common/base' })}
            </Button>
          </Box>
        )}
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable
          dataSource={canViewProducts ? inventoryList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsInventory(
            t,
            sortBy,
            handleSort,
            handleView,
            handleEdit,
            handleClone,
            handleArchive,
            canDetailProducts,
            canEditProducts
          )}
          onPageChange={handlePageChange}
          // dataSelectedIds={idsInventory}
          // onSelectChange={handleRowsSelect}
          {...(canDetailProducts ? { onRowClick: handleView } : {})}
        />
      </Box>

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isShowFormCloneProduct && <CloneProductForm productId={idInventory} onClose={handleCloseFormCloneProduct} />}

      {isLoading && <Loading />}

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}
    </Box>
  );
};

export default Inventory;
